import {
  CurrencyAddress,
  CurrencyModel,
  currencyRegistry,
} from '@root/entities/currency'
import { Network } from '@root/entities/network'

import { PremiumId } from './premium-id'
import { PremiumPurchaseId } from './premium-purchase-id'

import type * as PremiumMeta from './premium-meta'
import type { UserPurchaseHistoryListData } from '@blade/trade-api-client'
import type { TokenLogosHost } from '@root/shared/models'

type Dto = UserPurchaseHistoryListData['data'][number]

interface Common {
  id: PremiumPurchaseId
  network: Network.Network
  currencyAddress: CurrencyAddress
  currency: CurrencyModel | undefined
  createdAt: Date
  expiresAt?: Date | undefined
  purchaseTxHash?: string
  failedReason?: string
  failedAt: Date | undefined
  invoice: Dto['invoice']
}

export interface PremiumPurchase extends Common {
  type: 'premium'
  premiumId: PremiumId
  status: PremiumMeta.Status
  boostersAmount: number | undefined
  archivedAt: Date | undefined
  archivedReason: string | undefined
}

export interface BoostersPurchase extends Common {
  type: 'boosters'
  status: PremiumMeta.Status
  boostersAmount: number
  premiumPurchase: boolean
  premiumId: PremiumId | undefined
}

export type Purchase = PremiumPurchase | BoostersPurchase

export const fromDto = (
  dto: Dto,
  tokenLogosHost: TokenLogosHost,
): PremiumPurchase | BoostersPurchase => {
  const currency = dto.tokenMeta
    ? currencyRegistry.getOrCreate(
        CurrencyAddress(dto.tokenMeta.address),
        Network.fromDto(dto.tokenMeta.network),
        () => CurrencyModel.fromTokenMeta(dto.tokenMeta!, tokenLogosHost),
      )
    : undefined

  const common: Common = {
    id: PremiumPurchaseId(dto.id),
    network: Network.fromDto(dto.network),
    currencyAddress: CurrencyAddress(dto.tokenAddress),
    createdAt: new Date(dto.createdAt),
    expiresAt: dto.expiresAt ? new Date(dto.expiresAt) : undefined,
    purchaseTxHash: dto.purchaseTxHash,
    failedReason: dto.failedReason,
    failedAt: dto.failedAt ? new Date(dto.failedAt) : undefined,
    currency,
    invoice: dto.invoice,
  }

  if (dto.type === 'premium') {
    return {
      ...common,
      type: 'premium',
      premiumId: PremiumId(dto.id),
      status: dto.status as PremiumMeta.Status,
      boostersAmount: dto.boostersAmount,
      archivedAt: dto.archivedAt ? new Date(dto.archivedAt) : undefined,
      archivedReason: dto.archivedReason,
    }
  }

  return {
    ...common,
    type: 'boosters',
    status: dto.status as PremiumMeta.Status,
    boostersAmount: dto.boostersAmount ?? 0,
    premiumPurchase: dto.premiumPurchase ?? false,
    premiumId: dto.premiumId ? PremiumId(dto.premiumId) : undefined,
  }
}
